import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { DetailedCruise, DetailedDuck } from '@/interfaces/cruise';

export const useDemoCruiseStore = defineStore('demoCruise', () => {
  // Sample data for demo purposes
  const cruises = ref<DetailedCruise[]>([
    {
      id: 1,
      name: 'Caribbean Adventure',
      startDate: '2023-06-01',
      endDate: '2023-06-15',
      aboutUs: 'Join our exciting Caribbean cruise adventure! We will explore beautiful islands and enjoy the tropical weather.',
      socialLink: 'https://example.com/caribbean',
      emailRecipients: 'cruise@example.com,captain@example.com',
      ducks: [
        {
          id: "101",
          uuid: 'duck-uuid-101',
          name: 'Yellow Duck',
          found: 1,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 2
        },
        {
          id: "102",
          uuid: 'duck-uuid-102',
          name: 'Blue Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        },
        {
          id: "103",
          uuid: 'duck-uuid-103',
          name: 'Green Duck',
          found: 1,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 1
        },
        {
          id: "104",
          uuid: 'duck-uuid-104',
          name: 'Pink Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        },
        {
          id: "105",
          uuid: 'duck-uuid-105',
          name: 'Orange Duck',
          found: 1,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        },
        {
          id: "106",
          uuid: 'duck-uuid-106',
          name: 'Purple Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        },
        {
          id: "107",
          uuid: 'duck-uuid-107',
          name: 'Silver Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        },
        {
          id: "108",
          uuid: 'duck-uuid-108',
          name: 'Gold Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        },
        {
          id: "109",
          uuid: 'duck-uuid-109',
          name: 'Diamond Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        }
      ],
      unreadMessageCount: 3
    },
    {
      id: 2,
      name: 'Mediterranean Explorer',
      startDate: '2023-08-10',
      endDate: '2023-08-25',
      aboutUs: 'Discover the rich history and culture of the Mediterranean with our expert guides.',
      socialLink: 'https://example.com/mediterranean',
      emailRecipients: 'med@example.com',
      ducks: [
        {
          id: "201",
          uuid: 'duck-uuid-201',
          name: 'Red Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        },
        {
          id: "202",
          uuid: 'duck-uuid-202',
          name: 'Black Duck',
          found: 1,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 1
        },
        {
          id: "203",
          uuid: 'duck-uuid-203',
          name: 'White Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        },
        {
          id: "204",
          uuid: 'duck-uuid-204',
          name: 'Copper Duck',
          found: 0,
          image: '/ducksImages/duckIcon.webp',
          unreadMessageCount: 0
        }
      ],
      unreadMessageCount: 0
    }
  ]);

  // Get all cruises
  const getAllCruises = computed(() => cruises.value);

  // Get a cruise by ID
  const getCruiseById = computed(() => (id: number) => {
    return cruises.value.find(cruise => cruise.id === id) || null;
  });

  // Update a cruise
  const updateCruise = (cruiseData: Partial<DetailedCruise> & { id: number }) => {
    const index = cruises.value.findIndex(c => c.id === cruiseData.id);
    if (index !== -1) {
      // Merge the updated data with the existing cruise
      cruises.value[index] = {
        ...cruises.value[index],
        ...cruiseData,
        ducks: cruises.value[index].ducks // Preserve existing ducks
      };
      return true;
    }
    return false;
  };

  // Delete a cruise
  const deleteCruise = (id: number) => {
    const initialLength = cruises.value.length;
    cruises.value = cruises.value.filter(cruise => cruise.id !== id);
    return initialLength !== cruises.value.length;
  };

  // Add a duck to a cruise
  const addDuckToCruise = (cruiseId: number, duckData: { image: string }) => {
    const cruise = cruises.value.find(c => c.id === cruiseId);
    if (cruise) {
      const newId = Math.max(0, ...cruise.ducks.map(d => parseInt(d.id, 10))) + 1;
      const newDuck: DetailedDuck = {
        id: String(newId),
        uuid: `duck-uuid-${cruiseId}-${newId}`,
        name: `Duck ${newId}`,
        found: 0,
        image: duckData.image,
        unreadMessageCount: 0
      };
      cruise.ducks.push(newDuck);
      return newDuck;
    }
    return null;
  };

  // Reset all ducks in a cruise
  const resetCruiseDucks = (cruiseId: number) => {
    const cruise = cruises.value.find(c => c.id === cruiseId);
    if (cruise) {
      cruise.ducks.forEach(duck => {
        duck.found = 0;
      });
      return true;
    }
    return false;
  };

  // Reset a specific duck
  const resetDuck = (duckUuid: string) => {
    for (const cruise of cruises.value) {
      const duck = cruise.ducks.find(d => d.uuid === duckUuid);
      if (duck) {
        duck.found = 0;
        return true;
      }
    }
    return false;
  };

  // Find a duck by UUID
  const getDuckByUuid = (uuid: string): { duck: DetailedDuck, cruiseId: number } | null => {
    for (const cruise of cruises.value) {
      const duck = cruise.ducks.find(d => d.uuid === uuid);
      if (duck) {
        return { duck, cruiseId: cruise.id };
      }
    }
    return null;
  };

  // Update duck image
  const updateDuckImage = (duckUuid: string, imageData: string) => {
    const duckInfo = getDuckByUuid(duckUuid);
    if (duckInfo) {
      duckInfo.duck.image = imageData;
      return true;
    }
    return false;
  };

  return {
    cruises,
    getAllCruises,
    getCruiseById,
    updateCruise,
    deleteCruise,
    addDuckToCruise,
    resetCruiseDucks,
    resetDuck,
    getDuckByUuid,
    updateDuckImage
  };
});